import React, { useState } from "react"
import { MapPin, MoveRight } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import MapManager from "@/components/maps/MapManager"
import { ACTIONS, RouteStop, stopActionLabel } from "@/reducers/routes2gSlice"

const MOVE_PRESETS_KM = [10, 20, 50, 100]

/** Icon for a stop's action, mirroring the one shown on the route stop list. */
const StopActionIcon: React.FC<{ action?: string }> = ({ action }) => {
  const Icon = ACTIONS.find((item) => item.value === action)?.icon ?? MapPin
  return <Icon className="h-4 w-4 flex-shrink-0" />
}

/** "Stop 3" or "Stop 3 · Delivery" when the stop has a known action. */
const stopLabel = (t: TFunction, stop: RouteStop, index: number): string => {
  const action = stopActionLabel(t, stop.action)
  return action
    ? t("maps.moveDriverMenu.stopWithAction", { number: index + 1, action })
    : t("maps.moveDriverMenu.stopNumber", { number: index + 1 })
}

const truncate = (text: string, max = 40): string =>
  text.length > max ? `${text.substring(0, max)}...` : text

const stopAddress = (t: TFunction, stop: RouteStop, index: number): string =>
  stop.location?.formattedAddress ||
  stop.location?.companyName ||
  t("maps.moveDriverMenu.stopNumber", { number: index + 1 })

/** A stop we can actually move a driver to — it must carry coordinates. */
type MovableStop = { stop: RouteStop; index: number; lat: number; lon: number }

const toMovableStops = (stops: RouteStop[]): MovableStop[] =>
  stops
    .map((stop, index) => ({
      stop,
      index,
      lat: parseFloat(stop.location?.lat ?? ""),
      lon: parseFloat(stop.location?.lon ?? ""),
    }))
    .filter(
      ({ lat, lon }) => Number.isFinite(lat) && Number.isFinite(lon),
    ) as MovableStop[]

export type MoveDriverMenuProps = {
  mapManagerInstance: MapManager | null
  /** Shared with the parent context menu so click-outside detection still works. */
  menuRef: React.RefObject<HTMLDivElement>
  position: { x: number; y: number }
  /** Return to the main context menu. */
  onBack: () => void
  /** Close the whole context menu. */
  onClose: () => void
}

/**
 * Submenu for moving a driver forward along its current route — either by a
 * chosen distance, or straight to one of the stops on the route. Dispatches the
 * `slickshift-move-driver` event the SlickShift extension listens for; the
 * extension reports the outcome back via `slickshift-move-driver-result`
 * (handled by the parent WaypointContextMenu).
 */
const MoveDriverMenu: React.FC<MoveDriverMenuProps> = ({
  mapManagerInstance,
  menuRef,
  position,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation()
  const [customKm, setCustomKm] = useState("")

  const handleMoveDriver = (km: number) => {
    const routeJid = mapManagerInstance?.getRouteJid()
    if (!routeJid) {
      toast.error(t("maps.moveDriverMenu.noDriverSelected"))
      onClose()
      return
    }
    if (!Number.isFinite(km) || km <= 0) {
      toast.error(t("maps.moveDriverMenu.invalidDistance"))
      return
    }

    window.dispatchEvent(
      new CustomEvent("slickshift-move-driver", {
        detail: { driverJid: routeJid, km },
      }),
    )
    toast(t("maps.moveDriverMenu.movingByKm", { km }))
    onClose()
  }

  const handleCustomMove = () => {
    handleMoveDriver(parseFloat(customKm))
  }

  const handleMoveToStop = ({ stop, index, lat, lon }: MovableStop) => {
    const routeJid = mapManagerInstance?.getRouteJid()
    if (!routeJid) {
      toast.error(t("maps.moveDriverMenu.noDriverSelected"))
      onClose()
      return
    }

    const label = `${stopLabel(t, stop, index)} — ${stopAddress(t, stop, index)}`
    window.dispatchEvent(
      new CustomEvent("slickshift-move-driver", {
        detail: { driverJid: routeJid, lat, lon, label },
      }),
    )
    toast(t("maps.moveDriverMenu.movingToStop", { label }))
    onClose()
  }

  const movableStops = toMovableStops(
    mapManagerInstance ? mapManagerInstance.getStops() : [],
  )

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] min-w-[300px] max-w-lg overflow-hidden rounded-md bg-popover text-sm text-popover-foreground shadow-md"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="p-3">
        <div className="flex items-start gap-3 text-xs font-normal">
          <div className="mt-1 flex-shrink-0">
            <MoveRight className="h-4 w-4" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="font-medium">{t("maps.moveDriverMenu.title")}</div>
          </div>
        </div>
      </div>

      <div className="flex max-h-[70vh] flex-col overflow-y-auto border-t p-1">
        <button
          className="flex items-center gap-3 rounded-sm px-3 py-2 text-xs hover:bg-accent hover:text-accent-foreground"
          onClick={onBack}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t("common.back")}
        </button>

        {MOVE_PRESETS_KM.map((km) => (
          <button
            key={km}
            className="flex items-center gap-3 rounded-sm px-3 py-2 text-left text-xs hover:bg-accent hover:text-accent-foreground"
            onClick={() => handleMoveDriver(km)}
          >
            <MoveRight className="h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium">{km} km</div>
            </div>
          </button>
        ))}

        <div className="flex items-center gap-2 px-3 py-2">
          <input
            type="number"
            min="1"
            step="any"
            inputMode="decimal"
            placeholder={t("maps.moveDriverMenu.customKm")}
            value={customKm}
            onChange={(e) => setCustomKm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCustomMove()
            }}
            className="w-24 rounded border px-2 py-1 text-xs"
          />
          <button
            className="rounded-sm border px-3 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
            onClick={handleCustomMove}
          >
            {t("maps.moveDriverMenu.move")}
          </button>
        </div>

        {movableStops.length > 0 && (
          <>
            <div className="mt-1 border-t px-3 pb-1 pt-2 text-xs font-medium">
              {t("maps.moveDriverMenu.orToStop")}
            </div>
            {movableStops.map((movableStop) => (
              <button
                key={movableStop.stop.id ?? movableStop.index}
                className="flex items-center gap-3 rounded-sm px-3 py-2 text-left text-xs hover:bg-accent hover:text-accent-foreground"
                onClick={() => handleMoveToStop(movableStop)}
              >
                <StopActionIcon action={movableStop.stop.action} />
                <div className="flex-1">
                  <div className="font-medium">
                    {stopLabel(t, movableStop.stop, movableStop.index)}
                  </div>
                  <div className="text-muted-foreground">
                    {truncate(
                      stopAddress(t, movableStop.stop, movableStop.index),
                    )}
                  </div>
                </div>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

export default MoveDriverMenu
